import { useState, useEffect } from 'react';
import { Sidebar } from '../components/Sidebar';
import { AnalyticsCard } from '../components/AnalyticsComponents';
import { EngagementPieChart, HighlightedPieChart, TopEventsTable } from '../components/AnalyticsCharts';
import { EventPreview } from '../components/EventPreview';
import { Eye, Heart, UserCheck, Calendar, TrendingUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { eventService } from '../services/eventService';
import {
  OrganizerPendingView,
  OrganizerRejectedView,
  OrganizerViewerView,
  OrganizerErrorView,
} from '../components/OrganizerStatusViews';

export const OrganizerAnalytics = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewEvent, setPreviewEvent] = useState(null);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    const fetch = async () => {
      const data = await eventService.getEventsByOrganizer(user.uid);
      setEvents(data);
      setLoading(false);
    };
    fetch();
  }, [user]);

  // Calculate REAL stats from Firestore data
  const getTotalStats = () => {
    const totalEvents = events.length;
    
    // Sum all views (with safety check)
    const totalViews = events.reduce((sum, event) => {
      return sum + (event.views || 0);
    }, 0);
    
    // Sum all favorites (with safety check)
    const totalFavorites = events.reduce((sum, event) => {
      const favorites = event.favoriteUsers?.length || 0;
      return sum + favorites;
    }, 0);
    
    // Sum all confirmations (with safety check)
    const totalGoing = events.reduce((sum, event) => {
      const going = event.attendeesCount || 0;
      return sum + going;
    }, 0);

    return { 
      totalEvents, 
      totalViews, 
      totalFavorites, 
      totalGoing 
    };
  };

  const organizerStatus = user?.organizerStatus;

  if (organizerStatus && organizerStatus !== 'approved') {
    let statusView;
    if (organizerStatus === 'pending') {
      statusView = <OrganizerPendingView />;
    } else if (organizerStatus === 'rejected') {
      statusView = <OrganizerRejectedView />;
    } else if (organizerStatus === 'none' || organizerStatus === 'viewer') {
      statusView = <OrganizerViewerView />;
    } else {
      statusView = <OrganizerErrorView />;
    }
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <main className="flex-1 p-4 pt-16 md:p-8 flex items-center justify-center">
          {statusView}
        </main>
      </div>
    );
  }

  const stats = getTotalStats();

  // Calculate average engagement per event
  const avgEngagement = stats.totalEvents > 0
    ? Math.round((stats.totalViews + stats.totalFavorites + stats.totalGoing) / stats.totalEvents)
    : 0;

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <main className="flex-1 p-4 pt-16 md:p-8">
        <div className="max-w-7xl mx-auto space-y-8">
          <div>
            <h1 className="text-2xl md:text-3xl xl:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Analytics
            </h1>
            <p className="text-muted-foreground">
              Acompanhe o desempenho dos seus eventos com dados em tempo real
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="text-muted-foreground mt-4">Carregando analytics...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12 bg-card border border-border rounded-xl">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">Nenhum evento encontrado</h3>
              <p className="text-muted-foreground">Crie seu primeiro evento para ver analytics aqui!</p>
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <AnalyticsCard
                  title="Total de Eventos"
                  value={stats.totalEvents}
                  icon={Calendar}
                  color="primary"
                />
                <AnalyticsCard
                  title="Total de Visualizações"
                  value={stats.totalViews}
                  icon={Eye}
                  color="info"
                />
                <AnalyticsCard
                  title="Total de Favoritos"
                  value={stats.totalFavorites}
                  icon={Heart}
                  color="warning"
                />
                <AnalyticsCard
                  title="Total de Confirmações"
                  value={stats.totalGoing}
                  icon={UserCheck}
                  color="success"
                />
              </div>

              {/* Average Engagement Card */}
              <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 rounded-xl p-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Engajamento Médio por Evento</p>
                    <p className="text-3xl font-bold">{avgEngagement}</p>
                    <p className="text-xs text-muted-foreground">Interações totais / número de eventos</p>
                  </div>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <EngagementPieChart events={events} />
                <HighlightedPieChart events={events} />
              </div>

              {/* Top Events */}
              <TopEventsTable events={events} />

              {/* All Events Performance */}
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4">Todos os Eventos</h3>
                
                {events.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">Nenhum evento encontrado</p>
                ) : (
                  <div className="space-y-3">
                    {events
                      .sort((a, b) => {
                        // Sort by total engagement
                        const engagementA = (a.views || 0) + (a.favoriteUsers?.length || 0) + (a.attendeesCount || 0)
                        const engagementB = (b.views || 0) + (b.favoriteUsers?.length || 0) + (b.attendeesCount || 0)
                        return engagementB - engagementA;
                      })
                      .map((event) => {
                        const views = event.views || 0;
                        const favorites = event.favoriteUsers?.length || 0;
                        const going = event.attendeesCount || 0;
                        const totalEngagement = views + favorites + going;

                        // 🔥 TOP EVENT
                        const topEvent = [...events].sort((a, b) => {
                          const aEng = (a.views || 0) + (a.favoriteUsers?.length || 0) + (a.attendeesCount || 0);
                          const bEng = (b.views || 0) + (b.favoriteUsers?.length || 0) + (b.attendeesCount || 0);
                          return bEng - aEng;
                        })[0];

                        // 🚀 BOOST VS NORMAL
                        const boostedEvents = events.filter(e => e.boosted);
                        const normalEvents = events.filter(e => !e.boosted);

                        const avgBoostedViews = boostedEvents.reduce((sum, e) => sum + (e.views || 0), 0) / boostedEvents.length || 0;
                        const avgNormalViews = normalEvents.reduce((sum, e) => sum + (e.views || 0), 0) / normalEvents.length || 0;

                        const boostImpact = avgNormalViews > 0
                          ? Math.round((avgBoostedViews / avgNormalViews - 1) * 100)
                          : 0;

                        return (
                          <div
                            key={event.id}
                            onClick={() => setPreviewEvent(event)}
                            className="flex flex-wrap items-center gap-4 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 cursor-pointer transition-all"
                            data-testid={`analytics-event-${event.id}`}
                          >
                            {/* Event Info */}
                            <div className="flex-1 min-w-[180px]">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold truncate">{event.title}</h4>
                                {event.isHighlighted && (
                                  <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-300 rounded-full text-xs font-medium">
                                    Destaque
                                  </span>
                                )}
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  event.status === 'approved' 
                                    ? 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300'
                                    : event.status === 'pending'
                                    ? 'bg-yellow-100 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-300'
                                    : 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300'
                                }`}>
                                  {event.status === 'approved' ? 'Aprovado' : event.status === 'pending' ? 'Pendente' : 'Rejeitado'}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">{event.category} • {event.city}</p>
                            </div>

                            {/* Engagement Stats */}
                            <div className="flex items-center gap-4 shrink-0">
                              <div className="text-center">
                                <p className="text-xl font-bold text-blue-600">{views}</p>
                                <p className="text-xs text-muted-foreground">Views</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xl font-bold text-red-600">{favorites}</p>
                                <p className="text-xs text-muted-foreground">Favoritos</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xl font-bold text-green-600">{going}</p>
                                <p className="text-xs text-muted-foreground">Confirmados</p>
                              </div>
                              <div className="text-center border-l border-border pl-6">
                                <p className="text-xl font-bold">{totalEngagement}</p>
                                <p className="text-xs text-muted-foreground">Total</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>

      {previewEvent && (
        <EventPreview
          event={previewEvent}
          onClose={() => setPreviewEvent(null)}
        />
      )}
    </div>
  );
};

